import { createBrowserRouter } from 'react-router-dom';
import { FeatureGated } from './components/FeatureGated';
import { ProtectedShell } from './components/ProtectedShell';
import { AdminFeaturesRoute } from './routes/AdminFeaturesRoute';
import { AdminOrgBillingRoute } from './routes/AdminOrgBillingRoute';
import { AdminOrgGovernanceRoute } from './routes/AdminOrgGovernanceRoute';
import { AdminOrgRoute } from './routes/AdminOrgRoute';
import { AdminRimsRoute } from './routes/AdminRimsRoute';
import { AdminRoute } from './routes/AdminRoute';
import { AdminStatesRoute } from './routes/AdminStatesRoute';
import { AnalysesListRoute } from './routes/AnalysesListRoute';
import { AnalysisResultRoute } from './routes/AnalysisResultRoute';
import { AnalysisScoreRoute } from './routes/AnalysisScoreRoute';
import { CommunityHealthRoute } from './routes/CommunityHealthRoute';
import { DashboardRoute } from './routes/DashboardRoute';
import { HelpMethodologyRoute } from './routes/HelpMethodologyRoute';
import { HelpRoute } from './routes/HelpRoute';
import { IndustryTargetRoute } from './routes/IndustryTargetRoute';
import { LandingRoute } from './routes/LandingRoute';
import { NotFoundRoute } from './routes/NotFoundRoute';
import { OnboardingRoute } from './routes/OnboardingRoute';
import { PrintAnalysisRoute } from './routes/PrintAnalysisRoute';
import { SettingsApiKeysRoute } from './routes/SettingsApiKeysRoute';
import { SettingsAuditRoute } from './routes/SettingsAuditRoute';
import { SettingsBillingRoute } from './routes/SettingsBillingRoute';
import { SettingsGovernanceRoute } from './routes/SettingsGovernanceRoute';
import { SettingsJurisdictionRoute } from './routes/SettingsJurisdictionRoute';
import { SettingsMembersRoute } from './routes/SettingsMembersRoute';
import { SettingsMethodologyRoute } from './routes/SettingsMethodologyRoute';
import { SettingsOrganizationRoute } from './routes/SettingsOrganizationRoute';
import { SettingsRimsRoute } from './routes/SettingsRimsRoute';
import { SettingsSSORoute } from './routes/SettingsSSORoute';
import { SettingsScoringRoute } from './routes/SettingsScoringRoute';
import { SettingsWebhooksRoute } from './routes/SettingsWebhooksRoute';
import { SignInRoute } from './routes/SignInRoute';
import { SignUpRoute } from './routes/SignUpRoute';
import { StartTrialRoute } from './routes/StartTrialRoute';
import { WageBenchmarksRoute } from './routes/WageBenchmarksRoute';
import { WaitlistAdminRoute } from './routes/WaitlistAdminRoute';
import { WaitlistRoute } from './routes/WaitlistRoute';
import { WorkforceProfileRoute } from './routes/WorkforceProfileRoute';
import { FormSubmissionsRoute } from './routes/intake-forms/FormSubmissionsRoute';
import { IntakeFormBuilderRoute } from './routes/intake-forms/IntakeFormBuilderRoute';
import { IntakeFormPreviewRoute } from './routes/intake-forms/IntakeFormPreviewRoute';
import { IntakeFormsListRoute } from './routes/intake-forms/IntakeFormsListRoute';
import { SubmissionDetailRoute } from './routes/intake-forms/SubmissionDetailRoute';
import { ScenarioCompareRoute } from './routes/scenarios/ScenarioCompareRoute';
import { ScenarioEditorRoute } from './routes/scenarios/ScenarioEditorRoute';
import { ScenarioListRoute } from './routes/scenarios/ScenarioListRoute';
import { WizardRoute } from './routes/wizard/WizardRoute';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <LandingRoute />,
  },
  {
    path: '/sign-in/*',
    element: <SignInRoute />,
  },
  {
    path: '/sign-up/*',
    element: <SignUpRoute />,
  },
  {
    // Card-required reverse trial wall. Authenticated but intentionally outside
    // ProtectedShell so it renders full-screen without the app chrome the user
    // hasn't unlocked yet. ProtectedShell redirects walled orgs here.
    path: '/start-trial',
    element: <StartTrialRoute />,
  },
  {
    // Public waitlist capture page. Standalone, no app chrome.
    path: '/waitlist',
    element: <WaitlistRoute />,
  },
  {
    // Pathless layout — wraps all authenticated app routes
    element: <ProtectedShell />,
    children: [
      { path: 'onboarding', element: <OnboardingRoute /> },
      { path: 'dashboard', element: <DashboardRoute /> },
      { path: 'help', element: <HelpRoute /> },
      { path: 'help/methodology', element: <HelpMethodologyRoute /> },
      { path: 'analyses', element: <AnalysesListRoute /> },
      { path: 'analyses/new', element: <WizardRoute /> },
      { path: 'analyses/:id', element: <AnalysisResultRoute /> },
      { path: 'analyses/:id/score', element: <AnalysisScoreRoute /> },
      { path: 'analyses/:id/scenarios', element: <ScenarioListRoute /> },
      { path: 'analyses/:id/scenarios/:scenarioId', element: <ScenarioEditorRoute /> },
      { path: 'analyses/:id/compare', element: <ScenarioCompareRoute /> },
      { path: 'wage-benchmarks', element: <WageBenchmarksRoute /> },
      { path: 'workforce-profile', element: <WorkforceProfileRoute /> },
      { path: 'industry-targets', element: <IndustryTargetRoute /> },
      { path: 'community-health', element: <CommunityHealthRoute /> },
      { path: 'settings/organization', element: <SettingsOrganizationRoute /> },
      { path: 'settings/members', element: <SettingsMembersRoute /> },
      { path: 'settings/rims', element: <SettingsRimsRoute /> },
      { path: 'settings/jurisdiction', element: <SettingsJurisdictionRoute /> },
      { path: 'settings/methodology', element: <SettingsMethodologyRoute /> },
      { path: 'settings/scoring', element: <SettingsScoringRoute /> },
      { path: 'settings/governance', element: <SettingsGovernanceRoute /> },
      { path: 'settings/audit', element: <SettingsAuditRoute /> },
      {
        path: 'settings/api-keys',
        element: (
          <FeatureGated feature="api_keys">
            <SettingsApiKeysRoute />
          </FeatureGated>
        ),
      },
      {
        path: 'settings/webhooks',
        element: (
          <FeatureGated feature="webhooks">
            <SettingsWebhooksRoute />
          </FeatureGated>
        ),
      },
      { path: 'settings/billing', element: <SettingsBillingRoute /> },
      {
        path: 'settings/sso',
        element: (
          <FeatureGated feature="sso">
            <SettingsSSORoute />
          </FeatureGated>
        ),
      },
      {
        path: 'forms',
        element: (
          <FeatureGated feature="intake_forms">
            <IntakeFormsListRoute />
          </FeatureGated>
        ),
      },
      {
        path: 'forms/new',
        element: (
          <FeatureGated feature="intake_forms">
            <IntakeFormBuilderRoute />
          </FeatureGated>
        ),
      },
      {
        path: 'forms/:id/edit',
        element: (
          <FeatureGated feature="intake_forms">
            <IntakeFormBuilderRoute />
          </FeatureGated>
        ),
      },
      {
        path: 'forms/:id/preview',
        element: (
          <FeatureGated feature="intake_forms">
            <IntakeFormPreviewRoute />
          </FeatureGated>
        ),
      },
      {
        path: 'forms/:id/submissions',
        element: (
          <FeatureGated feature="intake_forms">
            <FormSubmissionsRoute />
          </FeatureGated>
        ),
      },
      {
        path: 'forms/:formId/submissions/:subId',
        element: (
          <FeatureGated feature="intake_forms">
            <SubmissionDetailRoute />
          </FeatureGated>
        ),
      },
    ],
  },
  {
    // Print route: auth-aware but no app shell — used for browser print-to-PDF
    path: '/print/analyses/:id',
    element: <PrintAnalysisRoute />,
  },
  {
    // Admin routes: Clerk-authenticated, no ProtectedShell sidebar
    path: '/admin',
    element: <AdminRoute />,
  },
  {
    path: '/admin/features',
    element: <AdminFeaturesRoute />,
  },
  {
    path: '/admin/waitlist',
    element: <WaitlistAdminRoute />,
  },
  {
    path: '/admin/orgs/:orgId',
    element: <AdminOrgRoute />,
  },
  {
    path: '/admin/orgs/:orgId/billing',
    element: <AdminOrgBillingRoute />,
  },
  {
    path: '/admin/orgs/:orgId/governance',
    element: <AdminOrgGovernanceRoute />,
  },
  {
    path: '/admin/rims',
    element: <AdminRimsRoute />,
  },
  {
    path: '/admin/states',
    element: <AdminStatesRoute />,
  },
  {
    path: '*',
    element: <NotFoundRoute />,
  },
]);
